// src/components/bot/InternalBotV2.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { botFetch } from './botApi';

// Format number with commas
const fmt = n => (n ?? '—').toLocaleString?.() ?? n;

// Build a bullet list from array
const bullets = (arr, fn) => arr.length ? arr.map(fn).join('\n') : '(none)';

// Generic data formatter for API responses
function formatGenericResponse(data) {
  if (!data) return "No data received.";
  if (typeof data === 'string') return data;
  
  // Try to find the array in the data (like data.cases, data.employees, etc)
  let items = [];
  let total = data.total ?? null;
  
  if (Array.isArray(data)) {
    items = data;
  } else if (data.json && Array.isArray(data.json)) {
    items = data.json;
  } else if (data.data && Array.isArray(data.data)) {
    items = data.data;
  } else {
    // Find the first array property
    for (const key in data) {
      if (Array.isArray(data[key])) {
        items = data[key];
        break;
      }
    }
  }

  if (items.length > 0) {
    const list = items.slice(0, 10).map(item => {
      // Just grab a few keys to display
      const keys = Object.keys(item).filter(k => k !== 'id' && !k.endsWith('_id') && item[k] !== null && typeof item[k] !== 'object');
      const str = keys.slice(0, 4).map(k => {
        let val = item[k];
        if (typeof val === 'number') val = fmt(val);
        return val;
      }).join(' | ');
      return `• ${str || 'Item'}`;
    }).join('\n');
    
    return `Results (${total ?? items.length} total):\n${list}`;
  }
  
  // Handle single object response (e.g. attendance stats)
  const statsObj = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
  if (statsObj && typeof statsObj === 'object' && !Array.isArray(statsObj)) {
    const keys = Object.keys(statsObj).filter(k => k !== 'success' && statsObj[k] !== null && typeof statsObj[k] !== 'object');
    if (keys.length > 0) {
      let title = data.date ? `Data for ${data.date}:\n` : "Details:\n";
      const list = keys.map(k => {
        let val = statsObj[k];
        if (typeof val === 'number') val = fmt(val);
        const label = k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, str => str.toUpperCase());
        return `• ${label}: ${val}`;
      }).join('\n');
      return title + list;
    }
  }
  
  // Fallback to JSON stringify
  const fallbackStr = JSON.stringify(data, null, 2);
  return "Data received:\n" + fallbackStr.substring(0, 500) + (fallbackStr.length > 500 ? '...' : '');
}

function Bubble({ msg }) {
  const isBot = msg.role === 'bot';
  return (
    <div style={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
      {isBot && (
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, marginRight: 7, marginTop: 1,
        }}>🤖</div>
      )}
      <div style={{
        maxWidth: '85%', padding: '6px 10px',
        borderRadius: isBot ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
        background: isBot ? '#f1f5f9' : 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
        color: isBot ? '#1e293b' : '#fff',
        fontSize: 11, lineHeight: 1.5,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      }}>
        {msg.text}
        {msg.loading && <span style={{ opacity: 0.5, animation: 'blink 1s step-start infinite' }}>▋</span>}
        
        {/* Render options if they are attached to this bot message */}
        {msg.options && msg.options.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {msg.options.map((opt, idx) => (
              <button
                key={opt.id || idx}
                onClick={() => msg.onOptionClick(opt)}
                disabled={msg.disabled}
                style={{
                  padding: '6px 10px', borderRadius: 8,
                  border: '1px solid #bfdbfe', background: '#fff',
                  color: '#1d4ed8', fontSize: 11, cursor: msg.disabled ? 'default' : 'pointer',
                  fontFamily: 'inherit', textAlign: 'center',
                  transition: 'background .12s',
                  opacity: msg.disabled ? 0.6 : 1,
                  flex: '1 1 auto'
                }}
              >
                {opt.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InternalBotV2() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [input, setInput] = useState('');
  const bottomRef = useRef();
  const inputRef = useRef();
  
  const rootMenuRef = useRef(null);
  const msgsRef = useRef(messages);
  useEffect(() => { msgsRef.current = messages; }, [messages]);

  // Scroll to bottom
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open, messages.length]);

  const showMainMenu = useCallback(() => {
    if (rootMenuRef.current) {
       setMessages(prev => [...prev, { 
          id: Date.now(), 
          role: 'bot', 
          text: 'Main Menu:',
          options: rootMenuRef.current,
          onOptionClick: handleOptionClick
        }]);
    }
  }, []); // Needs handleOptionClick logic internally but we can define handleOptionClick safely

  const handleOptionClick = useCallback(async (option) => {
    if (loading) return;
    
    // Disable previous options by updating the last bot message
    setMessages(prev => prev.map(m => m.options ? { ...m, disabled: true } : m));
    
    // Add user message
    const userMsgId = Date.now();
    setMessages(prev => [...prev, { id: userMsgId, role: 'user', text: option.name }]);
    
    // If it has sub-data, show them
    if (option.data && Array.isArray(option.data) && option.data.length > 0) {
      setTimeout(() => {
        let displayData = option.data;
        // Limit to first 3 options for the 2nd step
        if (option.id === 2 || option.id === '2') {
          displayData = option.data.slice(0, 3);
        }
        setMessages(prev => [...prev, { 
          id: Date.now(), 
          role: 'bot', 
          text: `Please select an option under **${option.name}**:`,
          options: displayData,
          onOptionClick: handleOptionClick
        }]);
      }, 300);
      return;
    }
    
    // If it has an endpoint, fetch it
    if (option.endpoint) {
      setLoading(true);
      
      // Add loading state
      const loadId = Date.now();
      setMessages(prev => [...prev, { id: loadId, role: 'bot', text: 'Fetching data...', loading: true }]);
      
      try {
        // Fix endpoint URL if it contains api/v1
        let url = option.endpoint;
        if (url.includes('api/v1/')) {
          url = url.split('api/v1/')[1];
        }
        if (!url.startsWith('/')) url = '/' + url;
        
        const r = await botFetch(url);
        
        setMessages(prev => prev.filter(m => m.id !== loadId)); // remove loading msg
        
        if (!r.ok) {
          setMessages(prev => [...prev, { id: Date.now(), role: 'bot', text: `Error fetching data: ${r.message}` }]);
        } else {
          // Format data
          const formatted = formatGenericResponse(r.data);
          setMessages(prev => [...prev, { id: Date.now(), role: 'bot', text: formatted }]);
        }
        
        // Show main menu again after a short delay
        setTimeout(() => {
          showMainMenu();
        }, 1000);
        
      } catch (e) {
        setMessages(prev => prev.filter(m => m.id !== loadId));
        setMessages(prev => [...prev, { id: Date.now(), role: 'bot', text: `Unexpected error: ${e.message}` }]);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // No data, no endpoint
    setTimeout(() => {
      setMessages(prev => [...prev, { id: Date.now(), role: 'bot', text: "This option is currently under construction." }]);
      setTimeout(() => showMainMenu(), 1000);
    }, 400);
    
  }, [loading, showMainMenu]);

  const findAllOptionsByName = (menu, text, results = []) => {
    const query = text.toLowerCase().trim();
    for (const opt of menu) {
      if (opt.name && opt.name.toLowerCase().includes(query)) {
        results.push(opt);
      }
      if (opt.data) {
        findAllOptionsByName(opt.data, text, results);
      }
    }
    return results;
  };

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    
    // Disable previous options by updating the last bot message
    setMessages(prev => prev.map(m => m.options ? { ...m, disabled: true } : m));
    
    // Find option matches
    const foundOptions = findAllOptionsByName(rootMenuRef.current || [], q);
    
    if (foundOptions.length > 0) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'user', text: q }]);
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          id: Date.now() + 2, 
          role: 'bot', 
          text: `Search results for "${q}":`,
          options: foundOptions,
          onOptionClick: handleOptionClick
        }]);
      }, 300);
    } else {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'user', text: q }]);
      setTimeout(() => {
        setMessages(prev => [...prev, { id: Date.now() + 2, role: 'bot', text: 'No matching option found for your search. Try typing a district name or a report name.' }]);
      }, 400);
    }
  }, [input, loading, handleOptionClick]);

  const onKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  // Fetch initial menu
  useEffect(() => {
    if (open && !initialFetchDone && !loading) {
      const fetchMenu = async () => {
        setLoading(true);
        setInitialFetchDone(true);
        try {
          setMessages([{ id: Date.now(), role: 'bot', text: 'Loading menu...', loading: true }]);
          const r = await botFetch('/chat-boat');
          
          if (!r.ok) {
            setMessages([{ id: Date.now(), role: 'bot', text: `Failed to load menu: ${r.message}` }]);
            return;
          }
          
          const menuOptions = Array.isArray(r.data) ? r.data : (r.data?.json || r.data?.data || []);
          rootMenuRef.current = menuOptions;
          
          setMessages([{ 
            id: Date.now(), 
            role: 'bot', 
            text: 'Hi! Please select an option from the menu below:',
            options: menuOptions,
            onOptionClick: handleOptionClick
          }]);
        } catch (e) {
          setMessages([{ id: Date.now(), role: 'bot', text: `Unexpected error: ${e.message}` }]);
        } finally {
          setLoading(false);
        }
      };
      fetchMenu();
    }
  }, [open, initialFetchDone, loading, handleOptionClick]);

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        title="VAHD Info Bot (V2)"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          width: 52, height: 52, borderRadius: '50%', border: 'none',
          background: open ? '#1e3a8a' : 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
          color: '#fff', fontSize: open ? 18 : 22, cursor: 'pointer',
          boxShadow: '0 4px 22px rgba(29,78,216,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform .2s, background .2s',
          transform: open ? 'rotate(45deg)' : 'none',
        }}
        aria-label="Toggle VAHD Info Bot"
      >
        {open ? '✕' : '🤖'}
      </button>

      {open && (
        <div style={{
          position: 'fixed', bottom: 86, right: 24, zIndex: 8999,
          width: 380, maxWidth: 'calc(100vw - 32px)',
          height: 520, maxHeight: 'calc(100vh - 130px)',
          background: '#fff',
          border: '1px solid rgba(226,232,240,0.9)',
          borderRadius: 20,
          boxShadow: '0 24px 64px rgba(15,23,42,0.2), 0 6px 20px rgba(29,78,216,0.12)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'dropIn .18s ease',
        }}>
          <div style={{
            padding: '12px 16px', flexShrink: 0,
            background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            }}>🤖</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>VAHD Info Bot (V2)</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>Live step-by-step assistant</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 16 }}
            >✕</button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', minHeight: 0 }}>
            {messages.map((m) => <Bubble key={m.id} msg={m} />)}
            <div ref={bottomRef} style={{ height: 10 }} />
          </div>

          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--bdr)', display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type option or district..."
              disabled={loading}
              style={{
                flex: 1, padding: '7px 12px', borderRadius: 20,
                border: '1.5px solid var(--bdr)', fontSize: 12,
                fontFamily: 'inherit', outline: 'none', background: '#fff',
                transition: 'border .15s',
              }}
              onFocus={e => { e.target.style.borderColor = '#3b82f6'; }}
              onBlur={e  => { e.target.style.borderColor = 'var(--bdr)'; }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none', flexShrink: 0,
                background: input.trim() && !loading ? 'linear-gradient(135deg,#1d4ed8,#3b82f6)' : '#e2e8f0',
                color: input.trim() && !loading ? '#fff' : '#94a3b8',
                fontSize: 15, cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background .15s',
              }}
              aria-label="Send"
            >➤</button>
          </div>

          {messages.length > 1 && (
            <div style={{ padding: '8px 10px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', gap: 6, flexShrink: 0, justifyContent: 'center' }}>
              <button
                onClick={showMainMenu}
                disabled={loading}
                style={{
                  padding: '6px 16px', borderRadius: 20,
                  border: '1px solid #e2e8f0', background: '#fff',
                  color: '#475569', fontSize: 12, cursor: 'pointer',
                  transition: 'all .2s'
                }}
              >
                Reset to Main Menu
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
